import { useCallback, useMemo, useState } from 'react';

import {
  AppDispatch,
  DEFAULT_LABEL_GROUP_COLOR,
  LabelSelectionGroup,
  LabelType,
  updatePreference,
} from '../../../../store';
import {
  buildSavedSelectionGroups,
  resolveCurrentGroupStyleTargetIds,
} from './editLabelTargets';

interface UseEditLabelGroupsParams {
  dispatch: AppDispatch;
  labels: LabelType[];
  preferenceGroups?: LabelSelectionGroup[];
  selectedLabelIds: string[];
}

export function useEditLabelGroups({
  dispatch,
  labels,
  preferenceGroups,
  selectedLabelIds,
}: UseEditLabelGroupsParams) {
  const [groupName, setGroupName] = useState('');
  const [groupColor, setGroupColor] = useState(DEFAULT_LABEL_GROUP_COLOR);

  const labelSelectionGroups = useMemo(() => preferenceGroups ?? [], [preferenceGroups]);
  const currentLabelGroup = useMemo(() => {
    return Object.values(preferenceGroups ?? {}).find((group) => {
      return selectedLabelIds
        .every((id) => group.labelIds.includes(id));
    }) ?? null;
  }, [selectedLabelIds, preferenceGroups]);

  const currentGroupStyleTargetIds = useMemo(
    () => resolveCurrentGroupStyleTargetIds(labels, currentLabelGroup),
    [labels, currentLabelGroup],
  );

  const handleSaveSelectionGroup = useCallback(() => {
    const trimmedName = groupName.trim();
    const nextGroups = buildSavedSelectionGroups(
      labelSelectionGroups,
      trimmedName,
      selectedLabelIds,
      groupColor,
    );

    if (!nextGroups) {
      return;
    }

    dispatch(updatePreference({ key: 'labelSelectionGroups', value: nextGroups }));
    setGroupName('');
  }, [dispatch, groupColor, groupName, labelSelectionGroups, selectedLabelIds]);

  const handleSaveGroupManager = useCallback((nextGroups: LabelSelectionGroup[]) => {
    dispatch(updatePreference({ key: 'labelSelectionGroups', value: nextGroups }));
  }, [dispatch]);

  return {
    groupName,
    setGroupName,
    groupColor,
    setGroupColor,
    labelSelectionGroups,
    currentLabelGroup,
    currentGroupStyleTargetIds,
    canSaveSelectionGroup: groupName.trim() !== '' && selectedLabelIds.length > 0,
    handleSaveSelectionGroup,
    handleSaveGroupManager,
  };
}

export default useEditLabelGroups;
